import { useState, useEffect, useRef, useCallback } from 'react';
import { audioPlaybackManager } from '../lib/audio-playback-manager';

/** 播放状态 */
export type AudioPlayerState = 'loading' | 'ready' | 'playing' | 'paused' | 'error';

/** 错误类型 */
export type AudioErrorType = 'unavailable' | 'unsupported' | null;

export interface UseAudioPlayerOptions {
  /** 音频 data URL */
  audioDataUrl: string | null;
  /** 播放结束回调 */
  onEnded?: () => void;
}

export interface UseAudioPlayerReturn {
  /** 当前播放状态 */
  state: AudioPlayerState;
  /** 当前播放位置（秒） */
  currentTime: number;
  /** 音频总时长（秒） */
  duration: number;
  /** 错误类型 */
  errorType: AudioErrorType;
  /** 播放 */
  play: () => void;
  /** 暂停 */
  pause: () => void;
  /** 跳转到指定位置 */
  seek: (time: number) => void;
}

/** 生成唯一播放器 ID */
let nextPlayerId = 0;
function generatePlayerId(): string {
  return `audio-player-${++nextPlayerId}`;
}

/**
 * 音频播放 Hook
 * 封装 HTML5 Audio API，集成 AudioPlaybackManager 实现全局单一播放
 */
export function useAudioPlayer(options: UseAudioPlayerOptions): UseAudioPlayerReturn {
  const { audioDataUrl, onEnded } = options;

  const [state, setState] = useState<AudioPlayerState>('loading');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [errorType, setErrorType] = useState<AudioErrorType>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playerIdRef = useRef<string>(generatePlayerId());
  const onEndedRef = useRef(onEnded);

  // 保持 onEnded 回调引用最新
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  // 初始化 Audio 元素并注册到 AudioPlaybackManager
  useEffect(() => {
    const playerId = playerIdRef.current;

    // 无 data URL 时标记为加载中
    if (!audioDataUrl) {
      setState('loading');
      return;
    }

    console.log('[useAudioPlayer] Loading audio, dataUrl length:', audioDataUrl.length, 'prefix:', audioDataUrl.substring(0, 60));

    const audio = new Audio();
    audioRef.current = audio;

    // 注册到全局播放管理器，被其他播放器抢占时自动暂停
    const unregister = audioPlaybackManager.register(playerId, () => {
      audio.pause();
      setState('paused');
      audioPlaybackManager.notifyStop(playerId);
    });

    // 音频元数据加载完成
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setState('ready');
    };

    // 播放进度更新
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    // 播放结束：重置进度条到起始位置
    const handleEnded = () => {
      setCurrentTime(0);
      audio.currentTime = 0;
      setState('ready');
      audioPlaybackManager.notifyStop(playerId);
      onEndedRef.current?.();
    };

    // 音频加载/解码错误
    const handleError = () => {
      const mediaError = audio.error;
      console.error('[useAudioPlayer] Audio error:', mediaError?.code, mediaError?.message, 'src length:', audio.src?.length);
      if (mediaError) {
        // MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED = 4
        if (mediaError.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
          setErrorType('unsupported');
        } else {
          setErrorType('unavailable');
        }
      } else {
        setErrorType('unavailable');
      }
      setState('error');
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    // 设置音频源，开始加载
    audio.src = audioDataUrl;

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
      audioRef.current = null;
      unregister();
    };
  }, [audioDataUrl]);

  // 播放
  const play = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || state === 'loading' || state === 'error') return;

    const playerId = playerIdRef.current;
    audioPlaybackManager.requestPlay(playerId);

    audio.play().then(() => {
      setState('playing');
    }).catch(() => {
      // 浏览器策略阻止自动播放时，恢复为 ready 状态
      setState('ready');
      audioPlaybackManager.notifyStop(playerId);
    });
  }, [state]);

  // 暂停
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    setState('paused');
    audioPlaybackManager.notifyStop(playerIdRef.current);
  }, []);

  // 跳转到指定位置
  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio || state === 'loading' || state === 'error') return;

    const clampedTime = Math.max(0, Math.min(time, audio.duration || 0));
    audio.currentTime = clampedTime;
    setCurrentTime(clampedTime);
  }, [state]);

  return {
    state,
    currentTime,
    duration,
    errorType,
    play,
    pause,
    seek,
  };
}
